import Dialog from "../../../../components/dialog/Dialog";
import DialogContent from "../../../../components/dialog/DialogContent";
import DialogTitle from "../../../../components/dialog/DialogTitle";
import DialogActions from "../../../../components/dialog/DialogActions";
import ButtonBar from "../../../../components/button/ButtonBar";
import {CancelButton} from "../../../../common/button/CommonButtons";
import React, {useEffect, useState} from "react";
import {Typography} from "@mui/material";
import {useClientLocator} from "../../../../client/ApiClientLocator";
import {useSnackbar} from "../../../../components/snackbar/Snackbar";
import {
    ContactRegistrationDataDto,
    ContactRegistrationRequestDto
} from "../../../../client/registration/RegistrationApiClient";
import {CommonTranslKey} from "../../../../common/CommonTranslKey";
import {RegistrationTranslKey} from "../../RegistrationTranslKey";
import {Trans, useTranslation} from "react-i18next";
import {ContactFormatter} from "../../../contact/ContactFormatter";
import {Cancel} from "@mui/icons-material";
import Button from "../../../../components/button/Button";
import {RegistrationEventType} from "../../RegistrationEventType";
import {useEventDispatcher} from "../../../../components/EventDispatcher";

enum RejectDialogState {
    IDLE,
    IN_PROGRESS,
}

export interface RegistrationRequestRejectDialogProps {
    open: boolean;
    onClose: (rejected: boolean) => void;
    registrationRequest?: ContactRegistrationRequestDto;
}

export default function RegistrationRequestRejectDialog(props: Readonly<RegistrationRequestRejectDialogProps>) {
    const {open, onClose, registrationRequest} = props;

    const [dialogState, setDialogState] = useState(RejectDialogState.IDLE);

    const {registrationClient} = useClientLocator();
    const {t} = useTranslation();
    const eventDispatcher = useEventDispatcher();
    const snackbar = useSnackbar();

    useEffect(() => {
        if (!open) {
            setDialogState(RejectDialogState.IDLE);
        }
    }, [open]);

    function rejectRegistration() {
        setDialogState(RejectDialogState.IN_PROGRESS);
        registrationClient.reject(registrationRequest!.id)
            .then(() => {
                snackbar.success(t(RegistrationTranslKey.REGISTRATION_REJECTED));
                eventDispatcher.dispatchEvent(new Event(RegistrationEventType.REQUEST_STATE_CHANGED));
                onClose(true);
            })
            .catch((error) => {
                snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                setDialogState(RejectDialogState.IDLE);
            });
    }

    function formatContactName(contactData: ContactRegistrationDataDto | undefined) {
        return contactData
            ? ContactFormatter.formatFirstNameAndLastName(contactData.firstName, contactData.lastName)
            : "";
    }

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth={"sm"}>
            <DialogTitle title={t(RegistrationTranslKey.REJECT_REGISTRATION)} />
            <DialogContent>
                <Typography variant={"body1"}>
                    <Trans i18nKey={RegistrationTranslKey.REJECT_REGISTRATION_PROMPT} values={{contactName: formatContactName(registrationRequest?.contactRegistrationData)}} />
                </Typography>
            </DialogContent>
            <DialogActions>
                <ButtonBar>
                    <CancelButton onClick={() => onClose(false)}/>
                    <Button icon={<Cancel />}
                            variant={"primary"}
                            color={"error"}
                            inProgress={dialogState === RejectDialogState.IN_PROGRESS}
                            onClick={rejectRegistration}>
                        {t(RegistrationTranslKey.REJECT)}
                    </Button>
                </ButtonBar>
            </DialogActions>
        </Dialog>
    );
}